import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Star, Video, User, Sparkles, EyeOff } from "lucide-react";
import { toast } from "sonner";
import VoiceInput from "@/components/VoiceInput";
import ReferenceChecksPanel from "@/components/ReferenceChecksPanel";
import BehavioralPanel from "@/components/BehavioralPanel";
import { FollowupQuestionsCard, FraudCheckCard, ClientSharesCard } from "@/components/CandidateSprintCards";
import CommitteeComments from "@/components/CommitteeComments";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StarRow = ({ value, onChange }) => (
  <div className="flex gap-1" data-testid="star-row">
    {[1,2,3,4,5].map(i => (
      <button key={i} type="button" onClick={() => onChange(i)}>
        <Star className={`w-5 h-5 ${i <= value ? "fill-pink text-pink" : "text-neutral-300"}`} style={{ fill: i <= value ? "#FF007F" : "transparent" }} />
      </button>
    ))}
  </div>
);

export default function CandidateDetail() {
  const { candidateId } = useParams();
  const [cand, setCand] = useState(null);
  const [job, setJob] = useState(null);
  const [scores, setScores] = useState({});
  const [rec, setRec] = useState("YES");
  const [comments, setComments] = useState("");
  const [saving, setSaving] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [blind, setBlind] = useState(() => localStorage.getItem("blindEval") === "1");
  const videoRef = useRef(null);

  const setBlindPersist = (v) => { setBlind(v); localStorage.setItem("blindEval", v ? "1" : "0"); };

  const jumpTo = (t) => {
    if (videoRef.current) {
      videoRef.current.currentTime = t;
      videoRef.current.play();
    }
  };

  const load = async () => {
    const c = await api.get(`/recruit/candidates/${candidateId}`);
    setCand(c.data);
    const j = await api.get(`/recruit/jobs/${c.data.job_id}`);
    setJob(j.data);
    if (c.data.video_path) {
      try {
        const resp = await api.get(`/files/download`, { params: { path: c.data.video_path }, responseType: "blob" });
        setVideoUrl(URL.createObjectURL(resp.data));
      } catch {}
    }
  };
  useEffect(() => { load(); }, [candidateId]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/recruit/candidates/${candidateId}/evaluate`, { scores, recommendation: rec, comments });
      toast.success("Evaluation submitted");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const moveHired = async () => {
    try { await api.patch(`/recruit/candidates/${candidateId}/stage`, { stage: "HIRED" }); toast.success("Moved to Hired · Onboarding launched"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  if (!cand || !job) return <div>Loading…</div>;
  const initials = cand.name.split(" ").map(x => x[0]).join("");
  const dispName = blind ? `Candidate ${cand.candidate_id.slice(-4).toUpperCase()}` : cand.name;
  const dispEmail = blind ? "hidden.for.fair.review" : cand.email;
  const dispInitials = blind ? "•" : initials;

  return (
    <div className="space-y-6" data-testid="candidate-detail-page">
      <Link to={`/app/recruit/jobs/${cand.job_id}`} className="text-sm text-neutral-500 hover:text-pink flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to {job.title}
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full grid place-items-center text-white text-lg font-bold" style={{ backgroundColor: blind ? "#777" : "#00214D" }}>{dispInitials}</div>
          <div>
            <h1 className="text-2xl font-bold" data-testid="candidate-display-name">{dispName}</h1>
            <div className="text-sm text-neutral-500">{dispEmail} · Applied for {job.title}</div>
            <div className="mt-2 flex gap-2">
              <Badge variant="outline">{cand.stage}</Badge>
              <Badge variant="outline">{cand.assessment_type}</Badge>
              {cand.ai_score != null && <Badge className="bg-pink text-white border-0">Candidate Score {cand.ai_score}</Badge>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 rounded-full bg-white border px-3 py-2" data-testid="blind-eval-toggle">
            <EyeOff className={`w-4 h-4 ${blind ? "text-pink" : "text-neutral-400"}`} />
            <Label className="text-xs cursor-pointer" onClick={() => setBlindPersist(!blind)}>Blind evaluation</Label>
            <Switch checked={blind} onCheckedChange={setBlindPersist} data-testid="blind-eval-switch" />
          </div>
          <Link to={`/app/interviews?candidate=${candidateId}`}>
            <Button variant="outline" className="rounded-full" data-testid="schedule-from-candidate-btn">Schedule interview</Button>
          </Link>
          <Button onClick={moveHired} disabled={cand.stage === "HIRED"} className="bg-teal text-white hover:opacity-90 rounded-full" data-testid="hire-btn">
            {cand.stage === "HIRED" ? "Hired" : "Mark as Hired"}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Left: video + AI summary */}
        <div className="lg:col-span-3 space-y-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4"><Video className="w-4 h-4 text-pink" /><h3 className="font-semibold">Video response</h3></div>
              {/* Question navigator tabs — real offsets from capture, or approximation */}
              {job?.questions?.length > 1 && cand.transcript_segments?.length > 0 && (
                <div className="mb-3 flex gap-1 flex-wrap" data-testid="question-nav-tabs">
                  {(() => {
                    const totalDur = cand.transcript_segments[cand.transcript_segments.length - 1]?.end || 0;
                    const qCount = job.questions.length;
                    const realOffsets = Array.isArray(cand.question_offsets) ? cand.question_offsets : null;
                    const hasReal = realOffsets && realOffsets.some(v => typeof v === "number");
                    return job.questions.map((q, i) => {
                      const real = hasReal ? realOffsets[i] : null;
                      const start = Math.round(typeof real === "number" ? real : (totalDur / qCount) * i);
                      const label = q.prompt ? q.prompt.slice(0, 40) + (q.prompt.length > 40 ? "…" : "") : `Question ${i+1}`;
                      const exact = typeof real === "number";
                      return (
                        <button
                          key={i}
                          onClick={() => jumpTo(start)}
                          data-testid={`q-tab-${i}`}
                          title={q.prompt}
                          className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1.5 transition-colors ${exact ? "border-teal/40 bg-teal/5 hover:bg-teal/10" : "border-neutral-200 hover:border-pink hover:bg-pink/5"}`}>
                          <span className={`font-mono font-bold ${exact ? "text-teal" : "text-pink"}`}>Q{i+1}</span>
                          <span className="hidden md:inline text-neutral-600 truncate max-w-[220px]">{label}</span>
                          <span className="font-mono text-[10px] text-neutral-400">{Math.floor(start/60)}:{String(start%60).padStart(2,"0")}</span>
                          {!exact && <span className="text-[9px] text-neutral-400" title="Approximate — captured before per-question timestamps were recorded">≈</span>}
                        </button>
                      );
                    });
                  })()}
                </div>
              )}
              {videoUrl ? (
                <VideoWithChapters
                  src={videoUrl}
                  videoRef={videoRef}
                  blind={blind}
                  questions={job?.questions}
                  offsets={cand.question_offsets}
                  transcriptEnd={cand.transcript_segments?.[cand.transcript_segments.length - 1]?.end}
                />
              ) : cand.video_path ? (
                <div className="text-neutral-500">Loading video…</div>
              ) : (
                <div className="rounded-lg bg-neutral-100 p-8 text-center text-neutral-500">No video submitted yet.</div>
              )}
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-pink" />
                <h3 className="font-semibold">Candidate Summary</h3>
                <Badge variant="outline" className="text-[9px] uppercase tracking-widest">AI · Resume + Video</Badge>
              </div>
              <div className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap" data-testid="ai-summary">
                {cand.ai_summary || "No Candidate Summary yet. It'll auto-generate once the candidate submits their video interview."}
              </div>
            </CardContent>
          </Card>

          <FollowupQuestionsCard candidateId={cand.candidate_id} />
          <FraudCheckCard candidateId={cand.candidate_id} />
          <ClientSharesCard candidateId={cand.candidate_id} candidateName={cand.name} />

          {cand.transcript_segments && cand.transcript_segments.length > 0 && (
            <Card className="border-0 shadow-sm" data-testid="transcript-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Video className="w-4 h-4 text-pink" />
                  <h3 className="font-semibold">Transcript</h3>
                  <Badge variant="outline" className="text-[10px]">Whisper</Badge>
                </div>
                <p className="text-xs text-neutral-500 mb-2">Click any line to jump to that moment in the video.</p>
                <div className="max-h-80 overflow-y-auto border rounded-lg divide-y" data-testid="transcript-list">
                  {cand.transcript_segments.map((s, i) => (
                    <button key={i} onClick={() => jumpTo(s.start)}
                      className="w-full text-left px-3 py-2 hover:bg-neutral-50 text-sm flex gap-3 items-start" data-testid={`transcript-seg-${i}`}>
                      <span className="font-mono text-xs text-pink shrink-0 pt-0.5">
                        {Math.floor(s.start / 60)}:{String(Math.floor(s.start % 60)).padStart(2, "0")}
                      </span>
                      <span className="text-neutral-700">{s.text}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: Scorecard */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-1">Scorecard</h3>
              <p className="text-xs text-neutral-500 mb-4">Weighted rubric (1–5). Weights sum to 100%.</p>
              <div className="space-y-4">
                {(job.scorecard_traits || []).map(t => (
                  <div key={t.id} className="border-b last:border-0 pb-3 last:pb-0">
                    <div className="flex justify-between">
                      <Label>{t.label}</Label>
                      <Badge variant="outline" className="text-[10px]">{t.weight}%</Badge>
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5">{t.description}</div>
                    <div className="mt-2"><StarRow value={scores[t.id] || 0} onChange={(v) => setScores({...scores, [t.id]: v})} /></div>
                  </div>
                ))}
              </div>
              <div className="mt-5">
                <Label>Recommendation</Label>
                <RadioGroup value={rec} onValueChange={setRec} className="grid grid-cols-2 gap-2 mt-2">
                  {["STRONG_NO","NO","YES","STRONG_YES"].map(v => (
                    <label key={v} className={`border rounded-lg p-2 text-xs text-center cursor-pointer ${rec === v ? "border-pink text-pink" : "text-neutral-500"}`}>
                      <RadioGroupItem value={v} className="sr-only" /> {v.replace(/_/g," ")}
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between mb-1.5">
                  <Label>Comments</Label>
                  <VoiceInput onTranscript={(t) => setComments(c => c ? `${c} ${t}` : t)} testId="comments-voice-btn" />
                </div>
                <Textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={3} data-testid="comments-input" />
              </div>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" onClick={() => { setScores({}); setRec("YES"); setComments(""); toast.success("Scorecard reset"); }}
                  className="rounded-full flex-1" data-testid="reset-scorecard-btn">Reset</Button>
                <Button onClick={submit} disabled={saving} className="btn-primary rounded-full flex-[2]" data-testid="submit-eval-btn">
                  {saving ? "Saving…" : "Submit evaluation"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {cand.evaluations?.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-3 flex items-center gap-2"><User className="w-4 h-4" /> Team evaluations</h3>
                <div className="space-y-3">
                  {cand.evaluations.map((e, i) => (
                    <div key={i} className="border-b last:border-0 pb-3 last:pb-0">
                      <div className="flex justify-between text-sm">
                        <span className="font-semibold">{e.evaluator_name}</span>
                        <span className="text-pink font-mono font-bold">{e.weighted_score}</span>
                      </div>
                      <div className="text-xs text-neutral-500">{e.recommendation.replace(/_/g," ")}</div>
                      {e.comments && <div className="text-sm mt-1 text-neutral-700">{e.comments}</div>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <ReferenceChecksPanel candidateId={candidateId} candidateName={cand.name} />
          <BehavioralPanel candidateId={candidateId} candidateName={cand.name} />
          <CommitteeComments candidateId={candidateId} candidateName={cand.name} />
        </div>
      </div>
    </div>
  );
}

function VideoWithChapters({ src, videoRef, blind, questions, offsets, transcriptEnd }) {
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [hover, setHover] = useState(null); // { q, x }

  // Choose real offsets if any, else even-divide across duration
  const hasReal = Array.isArray(offsets) && offsets.some(v => typeof v === "number");
  const dur = duration || transcriptEnd || 0;

  const chapters = (questions || []).map((q, i) => {
    let start;
    if (hasReal && typeof offsets[i] === "number") start = offsets[i];
    else start = dur ? (dur / (questions.length || 1)) * i : 0;
    return { i, q, start, approx: !(hasReal && typeof offsets[i] === "number") };
  });

  const onLoaded = () => { if (videoRef.current) setDuration(videoRef.current.duration || 0); };
  const onTime = () => { if (videoRef.current) setCurrent(videoRef.current.currentTime || 0); };

  const jumpChapter = (t) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = t;
    videoRef.current.play?.();
  };

  const fmt = (s) => `${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,"0")}`;

  return (
    <div className="relative" data-testid="video-with-chapters">
      <video
        ref={videoRef}
        src={src}
        controls
        onLoadedMetadata={onLoaded}
        onTimeUpdate={onTime}
        className={`w-full rounded-lg bg-black ${blind ? "filter blur-sm hover:blur-none transition-all" : ""}`}
        data-testid="candidate-video"
      />
      {chapters.length > 1 && dur > 0 && (
        <div className="mt-2" data-testid="video-chapter-track">
          <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-1 flex items-center gap-2">
            <span>Chapters</span>
            {!hasReal && <span className="text-neutral-400 normal-case">· approximate (older submission)</span>}
          </div>
          <div className="relative h-8 bg-neutral-100 rounded overflow-visible">
            {/* Current playhead progress */}
            <div className="absolute inset-y-0 left-0 bg-pink/10 rounded-l" style={{ width: `${Math.min(100, (current / dur) * 100)}%`, transition: "width 200ms linear" }} />
            {/* Chapter segments */}
            {chapters.map((c, i) => {
              const nextStart = chapters[i+1]?.start ?? dur;
              const leftPct = (c.start / dur) * 100;
              const widthPct = ((nextStart - c.start) / dur) * 100;
              const isCurrent = current >= c.start && current < nextStart;
              return (
                <div key={i} className="absolute inset-y-0" style={{ left: `${leftPct}%`, width: `${widthPct}%` }}>
                  {/* Divider on the left edge (except for the first chapter) */}
                  {i > 0 && <div className="absolute inset-y-0 left-0 w-px bg-neutral-300" />}
                  <button
                    onClick={() => jumpChapter(c.start + 0.05)}
                    onMouseEnter={() => setHover({ q: c, pct: leftPct + widthPct/2 })}
                    onMouseLeave={() => setHover(null)}
                    data-testid={`chapter-marker-${i}`}
                    className={`w-full h-full flex items-center justify-center text-[10px] font-mono font-bold transition-colors ${isCurrent ? "bg-pink/30 text-pink" : "hover:bg-pink/10 text-neutral-500 hover:text-pink"}`}
                    title={c.q?.prompt || `Question ${i+1}`}>
                    Q{i+1}
                  </button>
                </div>
              );
            })}
            {/* Hover tooltip */}
            {hover && (
              <div className="absolute -top-12 z-10 pointer-events-none" style={{ left: `${hover.pct}%`, transform: "translateX(-50%)" }}>
                <div className="bg-navy text-white text-[10px] px-2 py-1 rounded shadow-lg max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis">
                  <span className="text-pink font-bold">Q{hover.q.i+1}</span> · {fmt(hover.q.start)} · {hover.q.q?.prompt?.slice(0, 40) || "—"}{hover.q.q?.prompt?.length > 40 ? "…" : ""}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
